import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { ApiProvider } from "@src/store/ApiProvider";
import { withPasswordManagerRefresh } from "./passwordManagerRequest";

interface IDeleteVendorParams {
	vendorId: number;
}

interface IDeleteVendorState {
	loading: boolean;
	error: string | null;
}

const initialState: IDeleteVendorState = {
	loading: false,
	error: null,
};

const getAuthHeaders = () => {
	const token = localStorage.getItem("PasswordManager_accessToken") ?? "";
	return {
		Authorization: token ? `Bearer ${token}` : undefined,
		passwordManagerAccessToken: token || undefined,
	};
};

const buildUrl = (vendorId: number) => {
	const baseUrl = ApiProvider.default.passwordManagerBaseURL || "https://qa.stagefront.com/password-manager/api";
	return `${baseUrl.replace(/\/$/, "")}/Vendors/${vendorId}`;
};

export const deleteVendor = createAsyncThunk<undefined, IDeleteVendorParams>(
	"passwordManager/deleteVendor",
	async ({ vendorId }, { rejectWithValue, dispatch }) => {
		try {
			await withPasswordManagerRefresh(dispatch, () => axios.delete(buildUrl(vendorId), { headers: getAuthHeaders() }));
			return undefined;
		} catch (error: unknown) {
			if (axios.isAxiosError(error)) {
				if (error.response?.status === 409) {
					return rejectWithValue("Cannot delete vendor because it has credentials associated with it.");
				}
				const responseData = error.response?.data as { message?: string; title?: string } | string | undefined;
				const apiMessage =
					typeof responseData === "string" ? responseData : responseData?.message || responseData?.title || undefined;
				if (apiMessage) return rejectWithValue(apiMessage);
			}
			const message = error instanceof Error ? error.message : "Failed to delete vendor";
			return rejectWithValue(message);
		}
	},
);

const deleteVendorSlice = createSlice({
	name: "passwordManager/deleteVendor",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(deleteVendor.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteVendor.fulfilled, (state) => {
				state.loading = false;
				state.error = null;
			})
			.addCase(deleteVendor.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) || action.error.message || "Failed to delete vendor";
			});
	},
});

export const deleteVendorReducer = deleteVendorSlice.reducer;
